import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react';

/**
 * One hook for the two list-table behaviors every reviewer expects and this app used to give
 * inconsistently: click a header to sort, drag a header's right edge to resize the column. Built
 * once here (lifted from the Purchase Invoices line-items table and the Chart of Accounts page,
 * which each grew their own copy first) so every list in the gateway behaves the same way instead
 * of drifting page to page.
 *
 * Column widths are session-plus-localStorage state, exactly like the two originals: a missing or
 * corrupt stored value just falls back to the CSS/default width, so a private window or a cleared
 * store never breaks the page.
 */

export interface SortableColumnDef {
  minWidth?: number;
  defaultWidth?: number;
}

export interface UseSortableTableOptions<Key extends string> {
  /** localStorage key for persisted column widths. Omit to skip persistence entirely. */
  storageKey?: string;
  columns: Record<Key, SortableColumnDef>;
  defaultSortKey?: Key;
  defaultSortDir?: 'asc' | 'desc';
}

export interface UseSortableTableResult<Key extends string> {
  sortKey: Key | null;
  sortDir: 'asc' | 'desc';
  toggleSort: (key: Key) => void;
  /** Sorts a copy of `items` by the current sort key, using `valueOf` to read each row's value. */
  sort: <T>(items: T[], valueOf: (item: T, key: Key) => string | number | null | undefined) => T[];
  columnStyle: (key: Key) => CSSProperties | undefined;
  /** Attach to a resize handle's onMouseDown. Reads the real column width off the nearest <th>. */
  beginResize: (key: Key) => (event: ReactMouseEvent<HTMLElement>) => void;
  resizingKey: Key | null;
}

function loadStoredWidths<Key extends string>(
  storageKey: string | undefined,
  knownColumns: readonly Key[],
): Partial<Record<Key, number>> {
  if (!storageKey) return {};
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    const widths: Partial<Record<Key, number>> = {};
    for (const column of knownColumns) {
      const value = (parsed as Record<string, unknown>)[column];
      if (typeof value === 'number' && Number.isFinite(value)) widths[column] = value;
    }
    return widths;
  } catch {
    return {};
  }
}

function saveStoredWidths<Key extends string>(
  storageKey: string | undefined,
  widths: Partial<Record<Key, number>>,
): void {
  if (!storageKey) return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(widths));
  } catch {
    // Best-effort only - a private window or a full storage quota just means widths reset next visit.
  }
}

export function useSortableTable<Key extends string>(
  options: UseSortableTableOptions<Key>,
): UseSortableTableResult<Key> {
  const { storageKey, columns, defaultSortKey, defaultSortDir } = options;
  const knownColumns = useMemo(() => Object.keys(columns) as Key[], [columns]);

  const [sortKey, setSortKey] = useState<Key | null>(defaultSortKey ?? null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(defaultSortDir ?? 'asc');

  const [widths, setWidths] = useState<Partial<Record<Key, number>>>(() =>
    loadStoredWidths(storageKey, knownColumns),
  );
  const [resizingKey, setResizingKey] = useState<Key | null>(null);
  const dragOrigin = useRef<{ startX: number; startWidth: number } | null>(null);

  const toggleSort = useCallback(
    (key: Key) => {
      if (key === sortKey) {
        setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDir('asc');
      }
    },
    [sortKey],
  );

  const sort = useCallback(
    <T,>(items: T[], valueOf: (item: T, key: Key) => string | number | null | undefined): T[] => {
      if (!sortKey) return items;
      const key = sortKey;
      const withIndex = items.map((item, index) => ({ item, index }));
      withIndex.sort((a, b) => {
        const av = valueOf(a.item, key) ?? '';
        const bv = valueOf(b.item, key) ?? '';
        const cmp =
          typeof av === 'number' && typeof bv === 'number'
            ? av - bv
            : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
        // Stable: equal values keep their original relative order instead of shuffling on re-sort.
        return cmp !== 0 ? cmp : a.index - b.index;
      });
      const sorted = withIndex.map((w) => w.item);
      return sortDir === 'asc' ? sorted : sorted.reverse();
    },
    [sortKey, sortDir],
  );

  const columnStyle = useCallback(
    (key: Key): CSSProperties | undefined => {
      const width = widths[key] ?? columns[key]?.defaultWidth;
      return width !== undefined ? { width } : undefined;
    },
    [widths, columns],
  );

  const beginResize = useCallback(
    (key: Key) => (event: ReactMouseEvent<HTMLElement>) => {
      event.preventDefault();
      // event.currentTarget is the resize-handle element itself (a few px wide), not the column, so
      // its own offsetWidth is useless as a starting point - walk up to the actual header cell.
      const headerWidth = (event.currentTarget as HTMLElement).closest('th')?.getBoundingClientRect().width;
      const startWidth = widths[key] ?? headerWidth ?? columns[key]?.defaultWidth ?? columns[key]?.minWidth ?? 80;
      dragOrigin.current = { startX: event.clientX, startWidth };
      setResizingKey(key);
    },
    [widths, columns],
  );

  useEffect(() => {
    if (!resizingKey) return undefined;
    const min = columns[resizingKey]?.minWidth ?? 40;

    const onMouseMove = (event: MouseEvent) => {
      const origin = dragOrigin.current;
      if (!origin) return;
      const delta = event.clientX - origin.startX;
      const next = Math.max(min, Math.round(origin.startWidth + delta));
      setWidths((current) => ({ ...current, [resizingKey]: next }));
    };
    const onMouseUp = () => {
      dragOrigin.current = null;
      setResizingKey(null);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [resizingKey, columns]);

  // Persist once a drag ends, not on every pixel of movement.
  useEffect(() => {
    if (resizingKey) return;
    if (Object.keys(widths).length === 0) return;
    saveStoredWidths(storageKey, widths);
  }, [resizingKey, widths, storageKey]);

  return { sortKey, sortDir, toggleSort, sort, columnStyle, beginResize, resizingKey };
}
